'use strict';

import VZSpinner from 'Components/VZSpinner';
import * as debounce from 'lodash/debounce';
import { getUrlFromAttributes, appendParamToURL, getParamFromURL } from 'Util/urlUtils';
import { parseQuantity, toggleQuantityButtons } from './quantitySelectorUtils';

const PRODUCT_CARD_SELECTORS = 'vz-product-card, vz-tc-product-card';
const EXCLUDED_ATTRIBUTES = ['class', 'data-configurator-type', 'data-url', 'data-general-error'];

/**
 * @param {HTMLElement} card - The addon card element
 * @returns {number} The current quantity for the addon card
 */
function getCardQty(card) {
    return parseQuantity(card.getAttribute('data-qty'), 0);
}

/**
 * Manages Premium Connectivity addon quantities on PDP cards and drives variation updates.
 *
 * Cards use `<vz-quantity-selector>` (fires `fieldUpdated`; no `data-url` → no auto-fetch). This class
 * enforces the max across all PC cards and batches a single variation refresh.
 */
export default class VZPCAddonManager extends HTMLElement {
    connectedCallback() {
        this.spinner = document.querySelector('vz-spinner') || new VZSpinner();
        this.editMode = document.querySelector('.js-edit-mode');
        this.max = parseInt(this.getAttribute('data-max'), 10) || 0;
        this.baseAddons = JSON.parse(this.getAttribute('data-base-addons') || '[]');
        this.baseUrl = this.getAttribute('data-url');
        this.warningEl = this.querySelector('.js-pc-max-warning');
        this.cards = [...this.querySelectorAll('.pc-addon-card')];
        this.showMaxWarning = false;

        const debounceRate = parseFloat(this.getAttribute('debounce-rate')) || 400;
        this.debouncedVariationChangeDispatch = debounce(this.dispatchVariationChange.bind(this), debounceRate);

        this.cards.forEach((card) => {
            const field = card.querySelector('.js-quantity-field');
            if (!field) return;
            field.addEventListener('fieldUpdated', (evt) => this.onFieldUpdated(card, evt));
        });

        this.syncState();
    }

    /**
     * @returns {number} Total selected addon quantity across all cards.
     */
    getTotalQty() {
        return this.cards.reduce((sum, card) => sum + getCardQty(card), 0);
    }

    /**
     * Handles a `fieldUpdated` event from a wrapped `<vz-quantity-selector>` and clamps the
     * requested quantity against the remaining global addon capacity before triggering the
     * batched variation refresh.
     *
     * @param {HTMLElement} card - Addon card that owns the field.
     * @param {CustomEvent} evt - `fieldUpdated` event from the quantity field.
     * @returns {void}
     */
    onFieldUpdated(card, evt) {
        const field = evt.target;
        const previousOnCard = getCardQty(card);
        const requested = Math.max(0, parseQuantity(evt.detail && evt.detail.value, 0));

        const otherTotal = this.getTotalQty() - previousOnCard;
        const available = Math.max(0, this.max - otherTotal);
        const clamped = Math.min(requested, available);

        this.showMaxWarning = requested > available;

        if (clamped !== requested) {
            field.value = String(clamped);
            field.setAttribute('value', String(clamped));
        }

        if (clamped === previousOnCard) {
            this.syncState();
            return;
        }

        card.setAttribute('data-qty', clamped);
        this.syncState();
        this.debouncedVariationChangeDispatch();
    }

    /**
     * Synchronizes the card selection state and max warning visibility.
     *
     * @returns {void}
     */
    syncState() {
        this.cards.forEach((card) => {
            card.classList.toggle('info-card--bordered-primary', getCardQty(card) > 0);
        });

        if (this.warningEl) {
            this.warningEl.classList.toggle('d-none', !this.showMaxWarning);
        }
    }

    /**
     * Builds the addon payload expected by the variation endpoint.
     *
     * @returns {string[]} Flat list of addon SKUs, repeated per selected quantity.
     */
    buildAddonsArray() {
        const addons = [...this.baseAddons];

        this.cards.forEach((card) => {
            const sku = card.getAttribute('data-sku');
            const qty = getCardQty(card);
            for (let i = 0; i < qty; i++) {
                addons.push(sku);
            }
        });

        return addons;
    }

    /**
     * Requests updated variation markup and dispatches the variation change event.
     *
     * @returns {void}
     */
    dispatchVariationChange() {
        const vZProductCard = this.closest(PRODUCT_CARD_SELECTORS);
        if (!this.baseUrl || !vZProductCard) return;

        let url = getUrlFromAttributes(this.baseUrl, vZProductCard, EXCLUDED_ATTRIBUTES);
        url = appendParamToURL(url, 'addons', JSON.stringify(this.buildAddonsArray()));

        const productQuantityField = document.querySelector('.js-quantity-selector');
        if (productQuantityField) {
            url = appendParamToURL(url, 'quantity', productQuantityField.value);
        }

        if (this.editMode) {
            url = appendParamToURL(url, 'pliId', getParamFromURL(window.location.href, 'pliId'));
        }

        this.spinner.start();
        this.toggleAllButtons('disable');

        fetch(url)
            .then((res) => res.text())
            .then((response) => {
                vZProductCard.dispatchEvent(new CustomEvent('variationChange', { detail: { response: response } }));
            })
            .finally(() => {
                this.toggleAllButtons('enable');
                this.spinner.stop();
            });
    }

    /**
     * Enables or disables all +/- buttons managed by this component.
     *
     * @param {'enable' | 'disable'} action - State to apply.
     * @returns {void}
     */
    toggleAllButtons(action) {
        toggleQuantityButtons(this, action);
    }
}

if (!window.customElements.get('vz-pc-addon-manager')) {
    window.customElements.define('vz-pc-addon-manager', VZPCAddonManager);
}
